import placeholderImg from "../assets/placeholder.png";

/** API bases
 *  - RINGS_BASE: your rings router (unchanged; defaults to /rings or REACT_APP_RINGS_BASE)
 *  - API_ROOT: new backend root for auth/paypal/orders, e.g. http://localhost:6000/api
 */
const API_MODE = String(process.env.REACT_APP_API_MODE || "").toLowerCase();
const LOCAL_API_ROOT = process.env.REACT_APP_LOCAL_API_ROOT || "";
const AWS_API_ROOT = process.env.REACT_APP_AWS_API_ROOT || "";

export const API_ROOT =
  process.env.REACT_APP_API_ROOT ||
  (API_MODE === "aws" ? AWS_API_ROOT : LOCAL_API_ROOT);
export const RINGS_BASE =
  process.env.REACT_APP_RINGS_BASE ||
  (API_ROOT ? `${API_ROOT}/rings` : process.env.REACT_APP_API_BASE || "/rings");
export const REACT_APP_BRAND_NAME =
  process.env.REACT_APP_BRAND_NAME || "Ridley Jewelry";
export const PAYPAL_CLIENT_ID =
  process.env.REACT_APP_PAYPAL_CLIENT_ID || "test";
export const REACT_APP_GOLDAPI_KEY = process.env.REACT_APP_GOLDAPI_KEY || "";
export const PLACEHOLDER_RING_WEIGHTS_G = {
  default: 5,
};

export { placeholderImg };

export function currency(n) {
  if (n == null || isNaN(n)) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(n);
}

/** Fetch helpers */
export async function apiJson(base, path, opts = {}) {
  const optHeaders = opts.headers || {};
  const { headers: _ignored, ...rest } = opts;
  const res = await fetch(`${base}${path}`, {
    ...rest,
    headers: { "Content-Type": "application/json", ...optHeaders },
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      msg = j.error || JSON.stringify(j);
    } catch {}
    throw new Error(msg);
  }
  return res.json();
}

export async function fetchCatalog({ page = 1, limit = 24 } = {}) {
  return apiJson(RINGS_BASE, `/catalog?page=${page}&limit=${limit}`);
}

export async function fetchProduct(sku) {
  return apiJson(RINGS_BASE, `/${encodeURIComponent(sku)}`);
}

/**********************
 * Coupons, Shipping, Tax helpers (unchanged)
 *********************/
export const COUPONS = {
  SAVE10: { code: "SAVE10", type: "percent", value: 0.1, label: "Save 10%" },
  WELCOME15: {
    code: "WELCOME15",
    type: "percent",
    value: 0.15,
    label: "Welcome 15% off",
  },
  FREESHIP: {
    code: "FREESHIP",
    type: "freeship",
    value: 0,
    label: "Free Standard Shipping",
  },
};

export const SHIPPING_METHODS = [
  {
    id: "basic",
    label: "Standard Shipping (2-3 weeks for production)",
    cost: 0,
  },
  {
    id: "insured",
    label: "Insured Shipping (2-3 weeks for production)",
    cost: 40,
  },
];

export const TAX_RATES = {
  AL: 0.04,
  AK: 0.0,
  AZ: 0.056,
  AR: 0.065,
  CA: 0.0725,
  CO: 0.029,
  CT: 0.0635,
  DC: 0.06,
  DE: 0.0,
  FL: 0.06,
  GA: 0.04,
  HI: 0.04,
  ID: 0.06,
  IL: 0.0625,
  IN: 0.07,
  IA: 0.06,
  KS: 0.065,
  KY: 0.06,
  LA: 0.0445,
  ME: 0.055,
  MD: 0.06,
  MA: 0.0625,
  MI: 0.06,
  MN: 0.06875,
  MS: 0.07,
  MO: 0.04225,
  MT: 0.0,
  NE: 0.055,
  NV: 0.0685,
  NH: 0.0,
  NJ: 0.06625,
  NM: 0.05125,
  NY: 0.04,
  NC: 0.0475,
  ND: 0.05,
  OH: 0.0575,
  OK: 0.045,
  OR: 0.0,
  PA: 0.06,
  RI: 0.07,
  SC: 0.06,
  SD: 0.045,
  TN: 0.07,
  TX: 0.0625,
  UT: 0.0485,
  VT: 0.06,
  VA: 0.043,
  WA: 0.065,
  WV: 0.06,
  WI: 0.05,
  WY: 0.04,
};

export function getDiscount(subtotal, coupon) {
  if (!coupon) return 0;
  if (coupon.type === "percent") return +(subtotal * coupon.value).toFixed(2);
  if (coupon.type === "flat") return Math.min(subtotal, coupon.value);
  return 0;
}

export function getShippingCost(subtotal, shipping, coupon) {
  const method = shipping?.method || "basic";
  const base = SHIPPING_METHODS.find((m) => m.id === method)?.cost ?? 0.0;
  const freeThreshold = 300;
  if (coupon?.type === "freeship" && method === "basic") return 0;
  if (method === "basic" && subtotal >= freeThreshold) return 0;
  return base;
}

export function getTaxAmount(taxable, stateCode) {
  const rate = TAX_RATES[(stateCode || "").toUpperCase()] || 0;
  return +(taxable * rate).toFixed(2);
}

export function uniqueFromVariants(variants, key) {
  if (!Array.isArray(variants)) return null;
  const out = [];
  for (const v of variants) {
    const opts = v?.options || {};
    const val = opts[key];
    if (val != null && !out.includes(val)) out.push(val);
  }
  return out.length ? out : null;
}

export function pickVariant(product, wants) {
  const variants = product?.variants || [];
  if (!variants.length) return null;
  for (const v of variants) {
    const o = v.options || {};
    const kVal = o.karat || o.gold_karat;
    const ok =
      (wants.ring_size == null ||
        String(o.ring_size) === String(wants.ring_size)) &&
      (wants.metal_type == null ||
        String(o.metal_type) === String(wants.metal_type)) &&
      (wants.karat == null || String(kVal) === String(wants.karat)) &&
      (wants.stone_type == null ||
        String(o.stone_type) === String(wants.stone_type));
    if (ok) return v;
  }
  return variants[0];
}
